import React, { useEffect } from 'react';
import { RouteComponentProps } from 'react-router';
import { Route, useHistory } from 'react-router-dom';
import { getGroups } from '../AppShell/Authentication/AuthorisationAsync';
import { GroupIGroup } from '../AppShell/Authentication/AuthorisationModels';
import { RobotIcon } from '../Components/Icons';
import { LayoutCentral } from '../Components/Layout/LayoutCentral';
import { LoadingPlaceholder, Placeholder } from '../Components/Placeholder';
import { IColumnInfo, SimpleDataTable } from '../Components/SimpleDataTable/SimpleDataTable';
import { useAsync } from '../Hooks/useAsync';
import { useDebounce } from '../Hooks/useDebounce';
import { getGroupRoute, GroupRouteParams, routeForGroup, routeForNewGroup } from '../Routes';
import { useHttpConfig } from '../Shared/HttpRequestExecutor';
import { GroupFile } from './GroupFile';
import styles from './GroupList.module.scss';

const columnConfig: Array<IColumnInfo<GroupIGroup>> = [
    {
        key: 'name',
        title: 'Name',
        sort: ['name'],
        render: (row: GroupIGroup) => row.name || ''
    },
    {
        key: 'username',
        title: 'Group name',
        sort: ['username'],
        render: (row: GroupIGroup) => row.username || ''
    }
];

export function GroupList(props: { searchTerm: string }) {
    const history = useHistory();
    const searchTerm = props.searchTerm;
    const debouncedSearchTerm = useDebounce(searchTerm);
    const httpConfig = useHttpConfig();

    const groupsAsync = useAsync({
        defaultValue: [],
        httpConfig,
        getApi: getGroups
    });

    function refreshGroups() {
        groupsAsync.getApi(searchTerm);
    }

    useEffect(() => {
        refreshGroups();
    }, [debouncedSearchTerm]);

    function getResults() {
        if (groupsAsync.loadCount === 0 || groupsAsync.data === null)
            return (
                <LayoutCentral>
                    <LoadingPlaceholder />
                </LayoutCentral>
            );

        if (groupsAsync.loadCount > 0 && groupsAsync.data.length <= 0 && searchTerm === '') {
            return (
                <Placeholder
                    icon={<RobotIcon />}
                    actionText={'Create Group'}
                    action={newGroup}
                    infoText={'Whoops! There are no groups in the system. Would you like to:'}
                />
            );
        } else if (groupsAsync.loadCount > 0 && groupsAsync.data.length <= 0) {
            return (
                <Placeholder
                    icon={<RobotIcon />}
                    actionText={'Create Group'}
                    action={newGroup}
                    infoText={'Whoops! We could not find any records. Would you like to:'}
                />
            );
        }

        return (
            <SimpleDataTable<GroupIGroup>
                data={groupsAsync.data}
                enableScrollTo
                onRowSelection={onRowSelection}
                columns={columnConfig}
                selectedRows={[]}
            />
        );
    }

    const newGroup = () => history.push(routeForNewGroup);

    const onRowSelection = (rows: GroupIGroup[]) => {
        if (rows && rows.length) {
            history.push(getGroupRoute(rows[0].id));
        }
    };

    const renderGroupFile = (routerProps: RouteComponentProps<GroupRouteParams>) => (
        <GroupFile key='group-file' {...routerProps} onDataChanged={refreshGroups} />
    );

    const results = getResults();

    return (
        <div className={styles.flex}>
            {results}
            <Route path={routeForGroup} render={renderGroupFile} />
        </div>
    );
}
